/**
 * StaleLockMonitor
 *
 * Monitors AI locks across all tabs and shows a toast notification
 * when a lock becomes stale (no activity for the configured timeout period).
 */

import { useEffect, useRef } from 'react';
import { useAiLockStore, type AiLockInfo } from '../store/aiLockStore';
import { useAppStore } from '../../../store/appStore';
import { toast } from '../../../components/ui/toaster';
import { isLockStale } from '../utils/lockDurationUtils';

/**
 * Track which locks have already triggered a stale notification
 * to avoid showing duplicate toasts.
 */
const notifiedStaleLocks = new Set<string>();

export function StaleLockMonitor(): null {
  const lockedTabs = useAiLockStore((s) => s.lockedTabs);
  const lockTimeoutMinutes = useAppStore((s) => s.aiTerminalSettings.aiWorkspace.lockTimeoutMinutes);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Clear interval when no locks or timeout disabled
    if (lockedTabs.size === 0 || lockTimeoutMinutes <= 0) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    const checkStaleLocks = () => {
      for (const [tabId, lockInfo] of lockedTabs) {
        const stale = isLockStale(lockInfo.lastActivity, lockInfo.lockedAt, lockTimeoutMinutes);

        if (stale && !notifiedStaleLocks.has(tabId)) {
          notifiedStaleLocks.add(tabId);
          showStaleToast(tabId, lockInfo);
        }
      }
    };

    // Check immediately and then every 30 seconds
    checkStaleLocks();
    intervalRef.current = setInterval(checkStaleLocks, 30_000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [lockedTabs, lockTimeoutMinutes]);

  // Clean up notified locks when locks are removed
  useEffect(() => {
    const currentTabIds = new Set(lockedTabs.keys());
    for (const tabId of notifiedStaleLocks) {
      if (!currentTabIds.has(tabId)) {
        notifiedStaleLocks.delete(tabId);
      }
    }
  }, [lockedTabs]);

  return null;
}

function showStaleToast(
  _tabId: string,
  lockInfo: AiLockInfo
): void {
  const tabName = lockInfo.tabName ?? 'a tab';
  toast.warning({
    title: 'AI editing appears stale',
    description: `"${tabName}" has no AI activity. Use Force Unlock from tab context menu if needed.`,
    duration: 10_000,
  });
}
